/**
 * Landing Page V3 - Premium Split-Screen Login
 *
 * Full-screen split layout:
 * - Left: Technicians (field crew) - amber/gold tones
 * - Right: Office (command centre) - blue tones
 * - Mobile: Stacked vertically, each half is tappable
 *
 * WORKFLOW:
 * 1. Split Screen: TECHNICIANS (left) | OFFICE (right)
 * 2. User Selection: Slide-in panel with user grid
 * 3. PIN Entry: Overlay with numpad
 * 4. Navigation: Based on user role (office → /home, tech → /tech)
 */

import { getTenantIdSync } from '@/lib/tenant';
import { useState, useEffect, useMemo } from 'react';
import { setSessionIdentity, clearSessionIdentity, getSession, sessionRoleForUserRole } from '@/lib/session';
import {
  readPendingEmailLogin,
  writePendingEmailLogin,
  clearPendingEmailLogin,
} from '@/lib/pending-email-login';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { HardHat, Building2, ArrowLeft, Lock, UserCircle2, Wifi, Wrench, Monitor, Shield } from 'lucide-react';
import { User } from '../utils/seedData';
import { useTenant } from '@/hooks/useTenant';
import { setAuthExpiry } from '@/hooks/useTechAuth';
import { setSessionExpiry } from '@/hooks/useAuth';
import { EmailVerificationScreen } from '@/components/auth/EmailVerificationScreen';

// ============================================
// LAUNCH CELEBRATION — TEMPORARY (remove after launch week)
// ============================================
const LAUNCH_CELEBRATION = true; // Flip to false to disable

function LaunchAmbience() {
  // Floating golden dust particles — drift upward slowly
  const particles = useMemo(() =>
    Array.from({ length: 35 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      size: 1.5 + Math.random() * 2,
      duration: 12 + Math.random() * 18,
      delay: Math.random() * 20,
      drift: -20 + Math.random() * 40,
      opacity: 0.15 + Math.random() * 0.35,
    })),
  []);

  // Gold foil flakes — sparse, slow-falling, luxury confetti
  const foilFlakes = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: `${5 + Math.random() * 90}%`,
      width: 4 + Math.random() * 6,
      height: 3 + Math.random() * 4,
      fallDuration: 14 + Math.random() * 16,
      swayDuration: 4 + Math.random() * 4,
      delay: Math.random() * 20,
      initialRotation: Math.random() * 360,
      opacity: 0.2 + Math.random() * 0.35,
      gold: Math.random() > 0.3, // 70% gold, 30% light champagne
    })),
  []);

  // Soft ambient light orbs — large, blurred, barely visible
  const orbs = useMemo(() => [
    { id: 0, left: '15%', top: '25%', size: 280, opacity: 0.04, duration: 20, delay: 0 },
    { id: 1, left: '75%', top: '60%', size: 220, opacity: 0.035, duration: 25, delay: 5 },
    { id: 2, left: '50%', top: '15%', size: 320, opacity: 0.03, duration: 22, delay: 10 },
  ], []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[5]">
      {/* Golden dust particles rising */}
      {particles.map(p => (
        <div
          key={`p-${p.id}`}
          className="launch-particle"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            opacity: 0,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            '--particle-drift': `${p.drift}px`,
            '--particle-opacity': p.opacity,
          } as React.CSSProperties}
        />
      ))}

      {/* Gold foil flakes drifting down */}
      {foilFlakes.map(f => (
        <div
          key={`f-${f.id}`}
          className="launch-foil"
          style={{
            left: f.left,
            width: f.width,
            height: f.height,
            opacity: 0,
            background: f.gold
              ? 'linear-gradient(135deg, #c5a065, #e2c68a, #f5e6c8, #c5a065)'
              : 'linear-gradient(135deg, #e8dcc8, #f5e6c8, #fffbf0, #e8dcc8)',
            animationDuration: `${f.fallDuration}s, ${f.swayDuration}s`,
            animationDelay: `${f.delay}s, ${f.delay}s`,
            '--foil-opacity': f.opacity,
            transform: `rotate(${f.initialRotation}deg)`,
          } as React.CSSProperties}
        />
      ))}

      {/* Soft ambient light orbs */}
      {orbs.map(o => (
        <div
          key={`o-${o.id}`}
          className="launch-orb"
          style={{
            left: o.left,
            top: o.top,
            width: o.size,
            height: o.size,
            opacity: 0,
            animationDuration: `${o.duration}s`,
            animationDelay: `${o.delay}s`,
            '--orb-opacity': o.opacity,
          } as React.CSSProperties}
        />
      ))}

      {/* Single horizontal light sweep — luxury reveal */}
      <div className="launch-sweep" />
    </div>
  );
}

function LaunchBadge() {
  return (
    <div className="launch-badge-enter flex flex-col items-center mt-3">
      {/* Ornamental line above */}
      <div className="flex items-center gap-3 mb-1.5">
        <div className="w-8 md:w-12 h-px launch-ornament-line" />
        <div className="w-1 h-1 rounded-full bg-brand-secondary/50" />
        <div className="w-8 md:w-12 h-px launch-ornament-line" />
      </div>
      {/* Premium text badge */}
      <span className="launch-premium-text text-[10px] md:text-xs font-semibold tracking-[0.3em] uppercase">
        Now Live
      </span>
    </div>
  );
}
// ============================================
// END LAUNCH CELEBRATION
// ============================================

type View = 'MENU' | 'TECH_LIST' | 'OFFICE_LIST' | 'PIN_ENTRY' | 'EMAIL_VERIFICATION';
type LoginContext = 'tech' | 'office'; // Track which list the user came from

interface LandingPageUser {
  id: string;
  name: string;
  role: 'office' | 'sales' | 'ops' | 'tech' | 'provisioning' | 'guest_tech';
  dualAccess?: boolean; // User appears in both office and tech lists
  /** Whether this user has email-based passwordless login enabled.
   *  Plumbed through getActiveUsers Cloud Function (Phase 4.2 wires the
   *  Cloud Function side). When true, profile pick auto-sends an email and
   *  routes to the email verification screen instead of the PIN entry view.
   *  Defaults to false — falls back to PIN. */
  emailLoginEnabled?: boolean;
  // PIN intentionally omitted — never sent to the client. Validated server-side
  // by the `authenticateWithPin` Cloud Function. See: getActiveUsers in
  // functions/src/index.ts.
}

/** In-memory fallback for the per-browser email-login session id, used when
 *  localStorage is unavailable (iOS Lockdown Mode, blocked storage, full
 *  quota). Module-scoped so the request and the verify inside one document
 *  agree on a single value — minting a fresh id per call would make every
 *  code look like it came from a different device and get rejected. */
let inMemoryClientSessionId: string | null = null;

export function LandingPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  // Tier decides the shell. Defaults to Pro when unset — every existing tenant
  // predates the field and must keep the /home + /tech split.
  const isLiteTenant = tenant?.tier === 'lite';
  // Read once, synchronously, before first paint — resuming via an effect
  // would flash the profile picker on every return from the mail app.
  const [pendingEmailLogin] = useState(() =>
    readPendingEmailLogin(Boolean(getSession().displayName))
  );
  const [view, setView] = useState<View>(() =>
    pendingEmailLogin ? 'EMAIL_VERIFICATION' : 'MENU'
  );
  const [loginContext, setLoginContext] = useState<LoginContext | null>(
    () => pendingEmailLogin?.loginContext ?? null
  ); // Track which list user clicked from
  const [selectedUser, setSelectedUser] = useState<LandingPageUser | null>(
    () => pendingEmailLogin?.user ?? null
  );
  const [currentPin, setCurrentPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [welcomeUserName, setWelcomeUserName] = useState('');
  const [team, setTeam] = useState<LandingPageUser[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);
  const [teamLoadError, setTeamLoadError] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<string>('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [hoveredSide, setHoveredSide] = useState<'left' | 'right' | null>(null);

  // Email login flow state (Phase 4.2).
  // Populated when requestEmailLogin succeeds and we transition to the
  // EmailVerificationScreen. clientSessionId is the per-browser identifier
  // bound to the token doc so the verify endpoint can detect same-vs-cross-
  // device link clicks (see functions/src/auth/email-verification.ts).
  const [emailMaskedAddress, setEmailMaskedAddress] = useState<string>(
    () => pendingEmailLogin?.maskedEmail ?? ''
  );
  const [emailExpiresAt, setEmailExpiresAt] = useState<string>(
    () => pendingEmailLogin?.expiresAt ?? ''
  );
  const [isRequestingEmail, setIsRequestingEmail] = useState(false);
  const [emailRequestError, setEmailRequestError] = useState<string>('');

  // Map Firestore User role to LandingPage role
  // Load active users via the getActiveUsers Cloud Function.
  //
  // Pre-auth, Firestore rules deny any client read of `users`. The Cloud
  // Function returns ONLY {id, name, role, dualAccess?} with PINs and other
  // sensitive fields stripped server-side. It is rate-limited to 30 calls
  // per IP per hour. See: functions/src/index.ts → getActiveUsers.
  const loadActiveUsers = async () => {
    setIsLoadingTeam(true);
    setTeamLoadError(null);
    try {
      console.log('[LandingPage] Loading active users via Cloud Function...');

      const tenantId = getTenantIdSync();

      const getActiveUsers = httpsCallable<
        { tenantId: string },
        {
          users: Array<{
            id: string;
            name: string;
            role: User['role'];
            dualAccess?: boolean;
            // Phase 4.2 will start populating this from the Cloud Function.
            // Until then it'll be undefined → falls back to PIN flow, which
            // is the safe default for the migration window.
            emailLoginEnabled?: boolean;
          }>;
        }
      >(functions, 'getActiveUsers');

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout loading active users')), 30000)
      );

      const result = (await Promise.race([
        getActiveUsers({ tenantId }),
        timeoutPromise,
      ])) as {
        data: {
          users: Array<{
            id: string;
            name: string;
            role: User['role'];
            dualAccess?: boolean;
            emailLoginEnabled?: boolean;
          }>;
        };
      };

      const landingPageUsers: LandingPageUser[] = result.data.users.map((u) => ({
        id: u.id,
        name: u.name,
        role: sessionRoleForUserRole(u.role),
        dualAccess: u.dualAccess || false,
        emailLoginEnabled: u.emailLoginEnabled || false,
      }));

      console.log(`[LandingPage] ${landingPageUsers.length} active users available for login`);

      if (landingPageUsers.length === 0) {
        console.warn('[LandingPage] No users returned — check tenantId and seed data');
      }

      setTeam(landingPageUsers);
      setIsLoadingTeam(false);
    } catch (error: unknown) {
      console.error('[LandingPage] Error loading active users:', error);
      const e = error as { code?: string; details?: { code?: string } };
      const code = e?.code || e?.details?.code;
      if (code === 'functions/resource-exhausted') {
        setTeamLoadError('Too many login attempts from this network. Please try again in an hour.');
      } else {
        setTeamLoadError('Couldn\'t load team members. Check your connection and try again.');
      }
      setIsLoadingTeam(false);
    }
  };

  useEffect(() => {
    loadActiveUsers();
  }, []);

  // AUTO-REDIRECT: Check if user is already logged in
  useEffect(() => {
    const loggedInUser = localStorage.getItem('logged_in_user');
    const userRole = localStorage.getItem('user_role');

    if (loggedInUser && userRole) {
      console.log('[LandingPage] User already logged in, redirecting...', { user: loggedInUser, role: userRole });

      if (userRole === 'user' || !['office', 'sales', 'ops', 'tech', 'provisioning', 'guest_tech'].includes(userRole)) {
        console.warn('[LandingPage] Invalid or generic user role detected, clearing session');
        clearSessionIdentity();
        return;
      }

      setWelcomeUserName(loggedInUser);
      setShowWelcomeBack(true);
      localStorage.setItem('last_login', new Date().toISOString());
      setTimeout(() => setShowWelcomeBack(false), 3000);

      // A Lite tenant has ONE shell — no office/field split to pick between.
      if (isLiteTenant) {
        navigate('/app', { replace: true });
      } else if (userRole === 'office' || userRole === 'sales' || userRole === 'ops') {
        navigate('/home', { replace: true });
      } else if (userRole === 'tech' || userRole === 'guest_tech') {
        const techId = localStorage.getItem('tech_id') || '';
        const techName = localStorage.getItem('tech_name') || loggedInUser;
        navigate('/tech', { replace: true, state: { techId, techName } });
      } else if (userRole === 'provisioning') {
        navigate('/home/ops', { replace: true });
      }
    }
  }, [navigate, isLiteTenant]);

  // Reset PIN when view changes
  useEffect(() => {
    if (view !== 'PIN_ENTRY') {
      setCurrentPin('');
      setPinError(false);
    }
  }, [view]);

  // Auto-check PIN when 4 digits entered
  useEffect(() => {
    if (currentPin.length === 4) {
      checkPin();
    }
  }, [currentPin]);

  // Keyboard support for PIN entry
  useEffect(() => {
    if (view !== 'PIN_ENTRY') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key) || e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
      }
      if (/^[0-9]$/.test(e.key)) {
        enterDigit(parseInt(e.key));
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        backspaceDigit();
      } else if (e.key === 'Enter' && currentPin.length === 4) {
        checkPin();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, currentPin]);

  // Navigation handlers
  const showTechList = () => {
    setView('TECH_LIST');
    setLoginContext('tech');
  };
  const showOfficeList = () => {
    setView('OFFICE_LIST');
    setLoginContext('office');
  };
  const backToMenu = () => {
    setView('MENU');
    setSelectedUser(null);
    setLoginContext(null);
  };

  // ── Email login helpers (Phase 4.2) ─────────────────────────────────────
  //
  // The clientSessionId is a per-browser identifier stored once and reused
  // across every email-login attempt from this device. The Cloud Function
  // binds the token doc to this id so verifyEmailLoginToken can detect
  // same-vs-cross-device link clicks. Persistent so a user returning days
  // later to type a code still has the matching id. Cleared only on
  // explicit signOut (handled in useAuth.clearAllAuth).
  const getOrCreateClientSessionId = (): string => {
    const KEY = 'email_login_session_id';
    try {
      const existing = localStorage.getItem(KEY);
      if (existing) return existing;
    } catch {
      // Storage read blocked — fall through to the in-memory id.
    }
    if (inMemoryClientSessionId) return inMemoryClientSessionId;

    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    inMemoryClientSessionId = id;
    try {
      localStorage.setItem(KEY, id);
    } catch {
      // Blocked or full. The code path still works (request + verify happen
      // in this same document, sharing inMemoryClientSessionId). The link
      // path will read back nothing and land on the cross-device screen,
      // which is the correct outcome — previously this threw and killed the
      // profile pick outright.
    }
    return id;
  };

  // Shared post-auth state setup + role-based navigation. Called by both
  // the PIN flow (checkPin) and the email flow (handleVerifyEmailCode)
  // once they have a custom Firebase token in hand and signInWithCustomToken
  // has resolved. selectedUser drives the routing decision because it
  // carries the LandingPage-normalised lowercase role + dualAccess +
  // loginContext (which list the user clicked from).
  const finalizeSignIn = (signedInUser: { id: string; name: string; tenantId: string }) => {
    if (!selectedUser) return;

    // Sign-in landed — the resumable attempt is spent. Left behind, it would
    // resurrect the code screen on the next visit to the landing page.
    clearPendingEmailLogin();

    // Diagnostic — surfaces the dual-access routing decision in the
    // browser console. Useful when "I picked office, ended up on /tech"
    // reports come in. Safe to keep in production (no PII beyond name).
    console.log('[LandingPage] finalizeSignIn:', {
      name: selectedUser.name,
      role: selectedUser.role,
      dualAccess: selectedUser.dualAccess,
      loginContext,
      decision: selectedUser.dualAccess
        ? (loginContext === 'tech' ? 'dualAccess→tech' : 'dualAccess→office')
        : selectedUser.role === 'office' || selectedUser.role === 'sales' || selectedUser.role === 'ops'
        ? 'role→office'
        : selectedUser.role === 'tech' || selectedUser.role === 'guest_tech'
        ? 'role→tech'
        : 'no-match',
    });

    // Wave 2: identity keys go through lib/session (same key set as the
    // legacy inline writes); non-identity session bookkeeping stays here.
    const goesToTechSurface =
      (selectedUser.dualAccess && loginContext === 'tech') ||
      selectedUser.role === 'tech' ||
      selectedUser.role === 'guest_tech';
    setSessionIdentity({
      userId: signedInUser.id,
      userName: signedInUser.name,
      tenantId: signedInUser.tenantId,
      role: selectedUser.dualAccess
        ? (loginContext === 'tech' ? 'tech' : 'office')
        : selectedUser.role,
      ...(goesToTechSurface
        ? { techId: signedInUser.id, techName: signedInUser.name }
        : {}),
    });
    localStorage.setItem('last_login', new Date().toISOString());
    localStorage.setItem('session_persistent', 'true');
    setSessionExpiry();

    // Dual-access users route based on which list they came from. Office
    // staff land on /home. Tech + GuestTech land on /tech (which has its
    // own auth layer via TechAuthGuard).
    // Lite: one shell, so role doesn't decide where you land — everyone goes
    // to /app. This is also what removes the shell switching the split forced.
    if (isLiteTenant) {
      setAuthExpiry();
      navigate('/app', { replace: true });
    } else if (selectedUser.dualAccess) {
      if (loginContext === 'tech') {
        setAuthExpiry();
        navigate('/tech', { state: { techId: signedInUser.id, techName: signedInUser.name } });
      } else {
        navigate('/home');
      }
    } else if (
      selectedUser.role === 'office' ||
      selectedUser.role === 'sales' ||
      selectedUser.role === 'ops'
    ) {
      navigate('/home');
    } else if (selectedUser.role === 'tech' || selectedUser.role === 'guest_tech') {
      setAuthExpiry();
      navigate('/tech', { state: { techId: signedInUser.id, techName: signedInUser.name } });
    } else if (selectedUser.role === 'provisioning') {
      // Provisioning users work the /home/ops deployment board. Without this
      // branch they fell through the ladder with no navigate() and were
      // stranded on the landing page after sign-in (page reload self-heals
      // via the auto-redirect effect, but the first login looked broken).
      // Daily-audit #1001 C2.
      navigate('/home/ops');
    } else {
      // Defensive catch-all: never strand a signed-in user on the landing page.
      navigate('/home');
    }
  };

  /**
   * Friendly-message mapper for the Cloud Function HttpsError codes we
   * surface to end users on the email-login path. Keeps the call-site
   * handlers compact.
   */
  const mapEmailErrorCode = (err: unknown, defaultMsg: string): string => {
    const e = err as { code?: string; message?: string; details?: { code?: string } };
    const code = e?.code || e?.details?.code;
    if (code === 'functions/resource-exhausted') {
      return 'Too many sign-in emails sent. Try again in an hour.';
    }
    if (code === 'functions/failed-precondition') {
      // High-signal CF message (e.g. "No active sign-in code", "Email
      // login isn\'t configured for your organization"). Surface verbatim.
      return e?.message || defaultMsg;
    }
    if (code === 'functions/permission-denied') {
      return e?.message || 'Incorrect code. Try again.';
    }
    if (code === 'functions/invalid-argument') {
      return e?.message || 'Enter the 6-digit code from your email.';
    }
    if (code === 'functions/not-found') {
      return e?.message || 'Sign-in link not found or expired.';
    }
    if (code === 'functions/unavailable' || code === 'functions/internal') {
      return 'Server unavailable. Try again in a moment.';
    }
    return defaultMsg;
  };

  /**
   * Kick off the email-login flow for the picked user. Calls
   * requestEmailLogin on the Cloud Function, stores the returned masked
   * email + expiry, transitions to the EMAIL_VERIFICATION view.
   *
   * On failure we stay on the MENU view and surface the error via
   * emailRequestError. The most common failure mode here is the tenant
   * emailLoginConfig not being set up correctly — the CF's
   * failed-precondition message will say so.
   */
  const handleSendEmailLogin = async (user: LandingPageUser) => {
    setIsRequestingEmail(true);
    setEmailRequestError('');
    try {
      const clientSessionId = getOrCreateClientSessionId();
      const requestFn = httpsCallable<
        { userId: string; clientSessionId: string },
        { success: true; maskedEmail: string; expiresAt: string }
      >(functions, 'requestEmailLogin');
      const result = await requestFn({ userId: user.id, clientSessionId });
      setEmailMaskedAddress(result.data.maskedEmail);
      setEmailExpiresAt(result.data.expiresAt);
      writePendingEmailLogin({
        user,
        loginContext,
        maskedEmail: result.data.maskedEmail,
        expiresAt: result.data.expiresAt,
      });
      setView('EMAIL_VERIFICATION');
    } catch (err: unknown) {
      console.error('[LandingPage] requestEmailLogin failed:', err);
      setEmailRequestError(mapEmailErrorCode(err, "Couldn't send the sign-in email. Try again."));
    } finally {
      setIsRequestingEmail(false);
    }
  };

  /**
   * Verify the 6-digit code the user typed into EmailVerificationScreen.
   * The component throws on failure — its UI catches the throw and
   * surfaces the .message. On success we sign in via custom token and
   * navigate via finalizeSignIn.
   */
  const handleVerifyEmailCode = async (code: string): Promise<void> => {
    if (!selectedUser) throw new Error('Sign-in lost. Start again.');
    const clientSessionId = getOrCreateClientSessionId();
    const verifyFn = httpsCallable<
      { userId: string; code: string; clientSessionId: string },
      {
        success: true;
        customToken: string;
        user: {
          id: string;
          name: string;
          email: string;
          role: string;
          tenantId: string;
          dualAccess?: boolean;
        };
        emailVerifiedUntil: string;
      }
    >(functions, 'verifyEmailLoginCode');

    try {
      const result = await verifyFn({
        userId: selectedUser.id,
        code,
        clientSessionId,
      });
      const { customToken, user } = result.data;
      await signInWithCustomToken(auth, customToken);
      finalizeSignIn({ id: user.id, name: user.name, tenantId: user.tenantId });
    } catch (err: unknown) {
      console.error('[LandingPage] verifyEmailLoginCode failed:', err);
      throw new Error(mapEmailErrorCode(err, 'Verification failed. Try again.'));
    }
  };

  /**
   * Resend the sign-in email. Re-calls requestEmailLogin with the same
   * clientSessionId (so the new token doc is bound to the same browser).
   * The CF rate-limits to 3/hour per user; we surface that as a friendly
   * message. Returns the new maskedEmail + expiresAt so the verification
   * screen can refresh its countdown.
   */
  const handleResendEmailCode = async (): Promise<{
    maskedEmail: string;
    expiresAt: string;
  }> => {
    if (!selectedUser) throw new Error('Sign-in lost. Start again.');
    const clientSessionId = getOrCreateClientSessionId();
    const requestFn = httpsCallable<
      { userId: string; clientSessionId: string },
      { success: true; maskedEmail: string; expiresAt: string }
    >(functions, 'requestEmailLogin');
    try {
      const result = await requestFn({
        userId: selectedUser.id,
        clientSessionId,
      });
      setEmailMaskedAddress(result.data.maskedEmail);
      setEmailExpiresAt(result.data.expiresAt);
      writePendingEmailLogin({
        user: selectedUser,
        loginContext,
        maskedEmail: result.data.maskedEmail,
        expiresAt: result.data.expiresAt,
      });
      return {
        maskedEmail: result.data.maskedEmail,
        expiresAt: result.data.expiresAt,
      };
    } catch (err: unknown) {
      console.error('[LandingPage] resend requestEmailLogin failed:', err);
      throw new Error(mapEmailErrorCode(err, "Couldn't resend. Try again."));
    }
  };

  const handleBackFromEmail = () => {
    clearPendingEmailLogin();
    setView('MENU');
    setSelectedUser(null);
    setLoginContext(null);
    setEmailMaskedAddress('');
    setEmailExpiresAt('');
    setEmailRequestError('');
  };

  // ── Profile pick — branches on emailLoginEnabled ─────────────────────────
  // GuestTech always gets PIN flow. Non-guest with emailLoginEnabled=true
  // gets the email flow. Everything else falls back to PIN (during the
  // migration window before Phase 7 cutover flips emailLoginEnabled true
  // by default for all non-guests).
  const selectUser = (user: LandingPageUser) => {
    if (isRequestingEmail || isAuthenticating) return; // debounce
    console.log('[LandingPage] User selected:', user.name, `(${user.role}), context:`, loginContext, 'emailLogin:', user.emailLoginEnabled);
    setSelectedUser(user);
    setEmailRequestError('');
    // Drop any previously-resumable attempt. Without this, picking a PIN user
    // (or a different profile) would leave the old entry behind and a reload
    // would resume someone else's code screen.
    clearPendingEmailLogin();

    if (user.role === 'guest_tech') {
      // Guests never use email — defensive even if emailLoginEnabled were
      // somehow true on a guest record (UserModal forces it false).
      setView('PIN_ENTRY');
      return;
    }

    if (user.emailLoginEnabled === true) {
      void handleSendEmailLogin(user);
      return;
    }

    // Migration default — PIN flow continues to work for users not yet
    // opted into email login.
    setView('PIN_ENTRY');
  };

  const enterDigit = (digit: number) => {
    if (currentPin.length < 4) {
      setCurrentPin(currentPin + digit);
      setPinError(false);
    }
  };

  const backspaceDigit = () => {
    setCurrentPin(currentPin.slice(0, -1));
    setPinError(false);
  };

  // PIN validation — Cloud Function is the ONLY path. PINs are no longer
  // available client-side; client-side fallback validation has been removed.
  const checkPin = async () => {
    if (!selectedUser) return;

    setIsAuthenticating(true);
    setAuthStatus('Authenticating...');

    console.log('[LandingPage] Authenticating:', { user: selectedUser.name, role: selectedUser.role });

    try {
      setAuthStatus('Connecting to server...');
      const authenticateWithPin = httpsCallable<
        { userId: string; pin: string },
        { customToken: string; user: { id: string; name: string; role: string; tenantId: string } }
      >(functions, 'authenticateWithPin');

      const result = await authenticateWithPin({ userId: selectedUser.id, pin: currentPin });
      const { customToken, user } = result.data;

      setAuthStatus('Signing in...');
      await signInWithCustomToken(auth, customToken);

      console.log('[LandingPage] Authenticated via Cloud Function:', user.name);
      setAuthStatus('Success! Redirecting...');

      // Defer slightly so the "Success!" status flashes visually before
      // we navigate away. Routing decision + localStorage population live
      // in the shared finalizeSignIn helper so the PIN and email flows
      // can't drift apart.
      setTimeout(() => {
        finalizeSignIn({ id: user.id, name: user.name, tenantId: user.tenantId });
      }, 500);
    } catch (error: unknown) {
      // Cloud Function rejected (wrong PIN, inactive user, network error, rate limit).
      // PINs are not available client-side, so there is no fallback path.
      console.error('[LandingPage] Cloud Function auth failed:', error);

      const e = error as { code?: string; details?: { code?: string } };
      const code = e?.code || e?.details?.code;
      let message = 'Incorrect PIN';
      if (code === 'functions/resource-exhausted') {
        message = 'Too many attempts. Try later.';
      } else if (code === 'functions/unavailable' || code === 'functions/internal') {
        message = 'Server unavailable. Try again.';
      }

      setAuthStatus(message);
      setPinError(true);
      setIsAuthenticating(false);
      setTimeout(() => {
        setCurrentPin('');
        setPinError(false);
        setAuthStatus('');
      }, 1500);
    }
  };

  // Include dual-access users (Reuben, Guy) in both lists
  const techs = team.filter(m => m.role === 'tech' || m.role === 'guest_tech' || m.dualAccess);
  const officeStaff = team.filter(m => m.role === 'office' || m.role === 'sales' || m.role === 'ops' || m.dualAccess);

  return (
    <div className="force-dark fixed inset-0 z-[150] bg-brand-dark flex flex-col items-center px-6 text-white overflow-hidden">
      {/* Animated Mesh Gradient Blobs - contained to prevent overflow scroll */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="mesh-blob mesh-blob-1"></div>
        <div className="mesh-blob mesh-blob-2"></div>
        <div className="mesh-blob mesh-blob-3"></div>
      </div>

      {/* LAUNCH CELEBRATION — ambient golden particles + light sweep (temporary) */}
      {LAUNCH_CELEBRATION && <LaunchAmbience />}

      {/* Subtle Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}
      ></div>

      {/* Welcome Back Banner */}
      {showWelcomeBack && (
        <div className="fixed top-0 left-0 right-0 z-[200] safe-top pt-2 px-4 animate-slide-down">
          <div className="max-w-md mx-auto bg-gradient-to-r from-brand-secondary/90 to-yellow-600/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-brand-secondary/50 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <UserCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-lg">Welcome back, {welcomeUserName}!</p>
                <p className="text-white/80 text-sm">You're still logged in</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          LOADING STATE
          ================================================================ */}
      {isLoadingTeam && (
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white/80 text-lg">Loading team...</p>
          </div>
        </div>
      )}

      {!isLoadingTeam && teamLoadError && (
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-6">
          <div className="max-w-sm w-full text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
              <Lock className="w-6 h-6 text-red-300" />
            </div>
            <p className="text-white text-lg font-semibold mb-2">Couldn't load team</p>
            <p className="text-white/70 text-sm mb-6 leading-relaxed">{teamLoadError}</p>
            <button
              onClick={loadActiveUsers}
              className="px-6 py-3 bg-brand-secondary hover:bg-brand-secondary/90 text-slate-900 font-semibold rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* ================================================================
          VIEW: SPLIT SCREEN MENU
          ================================================================ */}
      {!isLoadingTeam && !teamLoadError && view === 'MENU' && (
        <div className="relative z-10 w-full h-full flex flex-col md:flex-row">
          {/* Central Logo - Floating over the split (desktop) */}
          <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex-col items-center pointer-events-none">
            <div className="relative breathing-logo">
              {/* LAUNCH CELEBRATION — soft golden aura (temporary) */}
              {LAUNCH_CELEBRATION && (
                <div className="absolute inset-[-28px] rounded-full launch-logo-aura" />
              )}
              <div className="absolute inset-[-12px] bg-brand-secondary/20 blur-2xl rounded-full animate-pulse"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-3xl flex items-center justify-center shadow-2xl border border-brand-secondary/30 ring-1 ring-brand-secondary/10 ring-offset-4 ring-offset-brand-dark">
                <svg className="w-14 h-14" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M50 20L85 40V45H15V40L50 20Z" className="fill-brand-secondary" />
                  <rect x="20" y="50" width="25" height="30" className="stroke-brand-secondary" strokeWidth="8" fill="none" />
                  <rect x="55" y="50" width="25" height="30" className="stroke-brand-secondary" strokeWidth="8" fill="none" />
                  <path d="M50 30L75 45H25L50 30Z" className="fill-brand-secondary" fillOpacity="0.3" />
                </svg>
              </div>
            </div>
            <div className="mt-3 text-center">
              <h1 className="text-xl font-bold tracking-wide bg-gradient-to-r from-white via-slate-100 to-white bg-clip-text text-transparent">
                {tenant?.companyAcronym || 'Operations Hub'}
              </h1>
              <p className="text-[9px] uppercase tracking-[0.2em] font-semibold text-white/40 mt-0.5">
                {tenant?.companyTaglineExpanded || tenant?.companyTagline || 'Operations Hub'}
              </p>
              {/* LAUNCH CELEBRATION — premium badge below logo (temporary) */}
              {LAUNCH_CELEBRATION && <LaunchBadge />}
            </div>
          </div>

          {/* Mobile Logo - Above the split */}
          <div className="flex md:hidden flex-col items-center pt-8 pb-4 safe-top relative z-20">
            <div className="relative breathing-logo mb-2">
              {/* LAUNCH CELEBRATION — soft golden aura mobile (temporary) */}
              {LAUNCH_CELEBRATION && (
                <div className="absolute inset-[-20px] rounded-full launch-logo-aura" />
              )}
              <div className="absolute inset-[-8px] bg-brand-secondary/20 blur-xl rounded-full animate-pulse"></div>
              <div className="relative w-16 h-16 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl flex items-center justify-center shadow-2xl border border-brand-secondary/20">
                <svg className="w-10 h-10" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M50 20L85 40V45H15V40L50 20Z" className="fill-brand-secondary" />
                  <rect x="20" y="50" width="25" height="30" className="stroke-brand-secondary" strokeWidth="8" fill="none" />
                  <rect x="55" y="50" width="25" height="30" className="stroke-brand-secondary" strokeWidth="8" fill="none" />
                  <path d="M50 30L75 45H25L50 30Z" className="fill-brand-secondary" fillOpacity="0.3" />
                </svg>
              </div>
            </div>
            <h1 className="text-lg font-bold tracking-wide bg-gradient-to-r from-white via-slate-100 to-white bg-clip-text text-transparent">
              {tenant?.companyAcronym || 'Operations Hub'}
            </h1>
            <p className="text-[8px] uppercase tracking-[0.2em] font-semibold text-white/40">
              {tenant?.companyTaglineExpanded || tenant?.companyTagline || 'Operations Hub'}
            </p>
            {/* LAUNCH CELEBRATION — premium badge below mobile logo (temporary) */}
            {LAUNCH_CELEBRATION && <LaunchBadge />}
          </div>

          {/* ============================================================
              LEFT SIDE - TECHNICIANS
              ============================================================ */}
          <button
            onClick={showTechList}
            onMouseEnter={() => setHoveredSide('left')}
            onMouseLeave={() => setHoveredSide(null)}
            className={`group relative flex-1 flex flex-col items-center justify-center overflow-hidden transition-all duration-700 ease-out cursor-pointer
              ${hoveredSide === 'left' ? 'md:flex-[1.15]' : hoveredSide === 'right' ? 'md:flex-[0.85]' : 'md:flex-1'}
            `}
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-950/30 via-transparent to-brand-dark/80 opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent opacity-80" />

            {/* Floating accent shapes */}
            <div className="absolute top-[15%] left-[10%] w-32 h-32 rounded-full bg-brand-secondary/[0.04] blur-2xl group-hover:bg-brand-secondary/[0.08] transition-all duration-1000" />
            <div className="absolute bottom-[20%] right-[15%] w-24 h-24 rounded-full bg-amber-600/[0.04] blur-xl group-hover:bg-amber-600/[0.08] transition-all duration-1000" />

            {/* Hover border edge */}
            <div className="absolute inset-0 border-r border-b md:border-b-0 border-white/[0.04] group-hover:border-brand-secondary/20 transition-colors duration-500" />

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center gap-4 md:gap-6 px-6 py-8 md:py-0 transform group-hover:scale-[1.02] transition-transform duration-500">
              {/* Icon cluster */}
              <div className="relative">
                <div className="absolute inset-[-20px] bg-brand-secondary/10 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative w-20 h-20 md:w-28 md:h-28 rounded-2xl md:rounded-3xl bg-gradient-to-br from-brand-secondary/20 to-amber-700/10 border border-brand-secondary/20 group-hover:border-brand-secondary/40 flex items-center justify-center shadow-2xl transition-all duration-500 group-hover:shadow-brand-secondary/10">
                  <HardHat className="w-10 h-10 md:w-14 md:h-14 text-brand-secondary drop-shadow-lg transition-transform duration-500 group-hover:scale-110" />
                </div>
                {/* Orbiting mini icons */}
                <div className="hidden md:flex absolute -top-2 -right-3 w-8 h-8 rounded-lg bg-brand-dark/80 border border-brand-secondary/20 items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:-translate-y-1">
                  <Wifi className="w-3.5 h-3.5 text-brand-secondary/70" />
                </div>
                <div className="hidden md:flex absolute -bottom-2 -left-3 w-8 h-8 rounded-lg bg-brand-dark/80 border border-brand-secondary/20 items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:translate-y-1">
                  <Wrench className="w-3.5 h-3.5 text-brand-secondary/70" />
                </div>
              </div>

              {/* Label */}
              <div className="text-center">
                <h2 className="text-2xl md:text-4xl font-black tracking-wider text-white mb-1 md:mb-2">
                  TECHNICIANS
                </h2>
                <p className="text-xs md:text-sm text-white/40 group-hover:text-white/60 transition-colors duration-500 font-medium tracking-wide">
                  Field Crew Login
                </p>
                <div className="mt-3 md:mt-4 flex items-center gap-2 justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                  <span className="text-[10px] md:text-xs text-brand-secondary/80 font-semibold uppercase tracking-widest">
                    {techs.length} {techs.length === 1 ? 'member' : 'members'}
                  </span>
                  <div className="w-5 h-px bg-brand-secondary/40" />
                  <span className="text-[10px] md:text-xs text-brand-secondary/60">Tap to enter</span>
                </div>
              </div>
            </div>

            {/* Bottom shimmer on hover */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-secondary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </button>

          {/* ============================================================
              RIGHT SIDE - OFFICE
              ============================================================ */}
          <button
            onClick={showOfficeList}
            onMouseEnter={() => setHoveredSide('right')}
            onMouseLeave={() => setHoveredSide(null)}
            className={`group relative flex-1 flex flex-col items-center justify-center overflow-hidden transition-all duration-700 ease-out cursor-pointer
              ${hoveredSide === 'right' ? 'md:flex-[1.15]' : hoveredSide === 'left' ? 'md:flex-[0.85]' : 'md:flex-1'}
            `}
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-bl from-blue-950/30 via-transparent to-brand-dark/80 opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent opacity-80" />

            {/* Floating accent shapes */}
            <div className="absolute top-[20%] right-[10%] w-32 h-32 rounded-full bg-blue-500/[0.04] blur-2xl group-hover:bg-blue-500/[0.08] transition-all duration-1000" />
            <div className="absolute bottom-[15%] left-[15%] w-24 h-24 rounded-full bg-cyan-500/[0.04] blur-xl group-hover:bg-cyan-500/[0.08] transition-all duration-1000" />

            {/* Hover border edge */}
            <div className="absolute inset-0 border-l border-t md:border-t-0 border-white/[0.04] group-hover:border-blue-400/20 transition-colors duration-500" />

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center gap-4 md:gap-6 px-6 py-8 md:py-0 transform group-hover:scale-[1.02] transition-transform duration-500">
              {/* Icon cluster */}
              <div className="relative">
                <div className="absolute inset-[-20px] bg-blue-500/10 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative w-20 h-20 md:w-28 md:h-28 rounded-2xl md:rounded-3xl bg-gradient-to-br from-blue-500/20 to-cyan-700/10 border border-blue-400/20 group-hover:border-blue-400/40 flex items-center justify-center shadow-2xl transition-all duration-500 group-hover:shadow-blue-500/10">
                  <Building2 className="w-10 h-10 md:w-14 md:h-14 text-blue-400 drop-shadow-lg transition-transform duration-500 group-hover:scale-110" />
                </div>
                {/* Orbiting mini icons */}
                <div className="hidden md:flex absolute -top-2 -left-3 w-8 h-8 rounded-lg bg-brand-dark/80 border border-blue-400/20 items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:-translate-y-1">
                  <Monitor className="w-3.5 h-3.5 text-blue-400/70" />
                </div>
                <div className="hidden md:flex absolute -bottom-2 -right-3 w-8 h-8 rounded-lg bg-brand-dark/80 border border-blue-400/20 items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:translate-y-1">
                  <Shield className="w-3.5 h-3.5 text-blue-400/70" />
                </div>
              </div>

              {/* Label */}
              <div className="text-center">
                <h2 className="text-2xl md:text-4xl font-black tracking-wider text-white mb-1 md:mb-2">
                  OFFICE
                </h2>
                <p className="text-xs md:text-sm text-white/40 group-hover:text-white/60 transition-colors duration-500 font-medium tracking-wide">
                  Command Centre Login
                </p>
                <div className="mt-3 md:mt-4 flex items-center gap-2 justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                  <span className="text-[10px] md:text-xs text-blue-400/80 font-semibold uppercase tracking-widest">
                    {officeStaff.length} {officeStaff.length === 1 ? 'member' : 'members'}
                  </span>
                  <div className="w-5 h-px bg-blue-400/40" />
                  <span className="text-[10px] md:text-xs text-blue-400/60">Tap to enter</span>
                </div>
              </div>
            </div>

            {/* Bottom shimmer on hover */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </button>
        </div>
      )}

      {/* ================================================================
          VIEW: TECH LIST
          ================================================================ */}
      {!isLoadingTeam && !teamLoadError && view === 'TECH_LIST' && (
        <div className="relative z-10 w-full h-full flex flex-col items-center overflow-y-auto">
          {/* Full-width amber accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-secondary to-transparent" />

          <div className="w-full max-w-lg px-6 safe-top pt-6 md:pt-12 pb-12">
            {/* Header */}
            <div className="flex items-center justify-center mb-8 md:mb-12 relative w-full">
              <button
                onClick={backToMenu}
                className="absolute left-0 h-11 w-11 flex items-center justify-center text-slate-300 hover:text-white bg-white/[0.04] backdrop-blur-md rounded-xl border border-white/[0.08] hover:border-brand-secondary/30 hover:scale-105 active:scale-95 transition-all duration-300 shadow-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-secondary/20 to-amber-700/10 border border-brand-secondary/20 flex items-center justify-center mx-auto mb-3">
                  <HardHat className="w-6 h-6 text-brand-secondary" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1">Select Technician</h3>
                <p className="text-xs text-white/30">Choose your profile to sign in</p>
              </div>
            </div>

            {/* Tech Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {techs.map((tech, index) => (
                <button
                  key={tech.id}
                  onClick={() => selectUser(tech)}
                  className="fade-in-up group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-105 active:scale-95"
                  style={{ animationDelay: `${index * 0.06}s` }}
                >
                  <div className="absolute inset-0 bg-white/[0.03] transition-all duration-300 group-hover:bg-white/[0.07]"></div>
                  <div className="absolute inset-0 rounded-2xl border border-white/[0.06] group-hover:border-brand-secondary/40 transition-all duration-300"></div>
                  <div className="relative flex flex-col items-center justify-center gap-3 py-7 px-3">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-brand-secondary/15 to-amber-700/10 border border-brand-secondary/20 group-hover:border-brand-secondary/40 flex items-center justify-center shadow-lg transition-all duration-300">
                      <UserCircle2 className="w-7 h-7 md:w-8 md:h-8 text-brand-secondary/80 group-hover:text-brand-secondary transition-colors duration-300" />
                    </div>
                    <span className="font-semibold text-white/80 group-hover:text-white text-sm transition-colors duration-300">{tech.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          VIEW: OFFICE LIST
          ================================================================ */}
      {!isLoadingTeam && !teamLoadError && view === 'OFFICE_LIST' && (
        <div className="relative z-10 w-full h-full flex flex-col items-center overflow-y-auto">
          {/* Full-width blue accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent" />

          <div className="w-full max-w-lg px-6 safe-top pt-6 md:pt-12 pb-12">
            {/* Header */}
            <div className="flex items-center justify-center mb-8 md:mb-12 relative w-full">
              <button
                onClick={backToMenu}
                className="absolute left-0 h-11 w-11 flex items-center justify-center text-slate-300 hover:text-white bg-white/[0.04] backdrop-blur-md rounded-xl border border-white/[0.08] hover:border-blue-400/30 hover:scale-105 active:scale-95 transition-all duration-300 shadow-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-700/10 border border-blue-400/20 flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1">Office Staff</h3>
                <p className="text-xs text-white/30">Choose your profile to sign in</p>
              </div>
            </div>

            {/* Office Staff Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {officeStaff.map((member, index) => (
                <button
                  key={member.id}
                  onClick={() => selectUser(member)}
                  className="fade-in-up group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-105 active:scale-95"
                  style={{ animationDelay: `${index * 0.06}s` }}
                >
                  <div className="absolute inset-0 bg-white/[0.03] transition-all duration-300 group-hover:bg-white/[0.07]"></div>
                  <div className="absolute inset-0 rounded-2xl border border-white/[0.06] group-hover:border-blue-400/40 transition-all duration-300"></div>
                  <div className="relative flex flex-col items-center justify-center gap-3 py-7 px-3">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-blue-500/15 to-cyan-700/10 border border-blue-400/20 group-hover:border-blue-400/40 flex items-center justify-center shadow-lg transition-all duration-300">
                      <UserCircle2 className="w-7 h-7 md:w-8 md:h-8 text-blue-400/80 group-hover:text-blue-400 transition-colors duration-300" />
                    </div>
                    <span className="font-semibold text-white/80 group-hover:text-white text-sm transition-colors duration-300">{member.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          VIEW: PIN ENTRY (Overlay)
          ================================================================ */}
      {!isLoadingTeam && !teamLoadError && view === 'PIN_ENTRY' && selectedUser && (
        <div
          className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-brand-dark/80 backdrop-blur-md"
          onClick={backToMenu}
        >
          <div
            className={`bg-brand-dark/98 backdrop-blur-2xl w-full max-w-sm rounded-3xl shadow-2xl p-8 text-center border border-white/[0.08] ${
              pinError ? 'shake' : ''
            }`}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            <div className="mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-secondary/20 to-amber-700/10 border border-brand-secondary/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-brand-secondary drop-shadow-lg" />
              </div>
              <h3 className="text-white text-2xl font-semibold mb-1">Enter PIN</h3>
              <p className="text-white/40 text-sm tracking-wide mt-2">{selectedUser.name}</p>

              {authStatus && (
                <p className={`text-sm mt-3 font-medium ${
                  authStatus.includes('Incorrect') ? 'text-red-400' :
                  authStatus.includes('Success') ? 'text-emerald-400' :
                  'text-brand-secondary'
                }`}>
                  {isAuthenticating && !authStatus.includes('Incorrect') && !authStatus.includes('Success') && (
                    <span className="inline-block w-4 h-4 border-2 border-brand-secondary border-t-transparent rounded-full animate-spin mr-2 align-middle"></span>
                  )}
                  {authStatus}
                </p>
              )}
            </div>

            {/* PIN Dots */}
            <div className="flex justify-center gap-4 mb-10 h-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`pin-dot ${i < currentPin.length ? 'filled' : ''}`}></div>
              ))}
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-4 max-w-[260px] mx-auto justify-items-center">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                <button key={digit} onClick={() => enterDigit(digit)} className="keypad-btn">
                  {digit}
                </button>
              ))}
              <button
                onClick={backToMenu}
                className="keypad-btn !text-[11px] !font-medium !bg-transparent !border-0 text-white/30 hover:text-white/60 active:scale-95"
              >
                CANCEL
              </button>
              <button onClick={() => enterDigit(0)} className="keypad-btn">
                0
              </button>
              <button
                onClick={backspaceDigit}
                className="keypad-btn !text-2xl !bg-transparent !border-0 text-white/30 hover:text-white/60 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Email login: "Sending email…" loading overlay ─────────────────
          Shown for the 1-3s between profile pick and the verification
          screen appearing. Without this the profile picker just sits
          there with no feedback, which feels broken on slow connections. */}
      {isRequestingEmail && (
        <div className="force-dark fixed inset-0 z-[170] flex items-center justify-center bg-brand-dark/80 backdrop-blur-md text-white">
          <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.1] rounded-2xl px-8 py-6 flex items-center gap-3 shadow-2xl">
            <div className="w-5 h-5 border-2 border-brand-secondary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Sending your sign-in email…</span>
          </div>
        </div>
      )}

      {/* ── Email login: error banner ──────────────────────────────────
          Surfaces requestEmailLogin failures (tenant misconfigured,
          rate-limited, etc.) when the user is still on the MENU view.
          Auto-dismissible — clicking the X clears it but the next
          profile pick will also clear it. */}
      {emailRequestError && view === 'MENU' && (
        <div className="force-dark fixed top-6 left-1/2 -translate-x-1/2 z-[200] max-w-md w-[calc(100%-3rem)]">
          <div className="bg-rose-500/15 backdrop-blur-xl border border-rose-400/40 rounded-xl px-4 py-3 flex items-start gap-3 shadow-2xl">
            <Lock className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-rose-100">{emailRequestError}</div>
            <button
              type="button"
              onClick={() => setEmailRequestError('')}
              className="text-rose-200 hover:text-white text-sm"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Email verification view ────────────────────────────────────
          Mounted only when requestEmailLogin succeeded — emailMaskedAddress
          and emailExpiresAt are populated. The component owns its own
          state and calls back into the handlers for verify/resend/back. */}
      {view === 'EMAIL_VERIFICATION' && selectedUser && emailMaskedAddress && (
        <EmailVerificationScreen
          userName={selectedUser.name}
          maskedEmail={emailMaskedAddress}
          expiresAt={emailExpiresAt}
          onVerifyCode={handleVerifyEmailCode}
          onResend={handleResendEmailCode}
          onBack={handleBackFromEmail}
        />
      )}
    </div>
  );
}
